import { Button, Combobox, type ComboboxItem, Group, ScrollArea, Text, useCombobox } from "@mantine/core"
import { IconCheck, IconChecks, IconX } from "@tabler/icons-react"
import { useTranslation } from "react-i18next"
import { useStateDeps } from "@/hooks"

type TComboboxGroup = {
  group: string
  items: ComboboxItem[]
}

interface IProps {
  label: string
  value: string
  setValue: (value?: string) => void
  loading?: boolean
  items: ComboboxItem[]
  groups?: TComboboxGroup[]
}

export function SelectFilter({
  items: values = [],
  groups = [],
  value: source,
  setValue: setSource,
  label,
  loading,
}: IProps) {
  const { t } = useTranslation()

  const combobox = useCombobox()
  const [value, setValue] = useStateDeps<string | undefined>(source)
  const allItems = groups.length > 0 ? groups.flatMap((group) => group.items) : values

  return (
    <Combobox
      store={combobox}
      withArrow
      withOverlay
      arrowPosition="side"
      onOptionSubmit={(val) => (value !== val ? setValue(val) : setValue(undefined))}
      onClose={() => {
        if (value !== source) setSource(value)
      }}
    >
      <Combobox.Target>
        <Button
          size="compact-xs"
          loading={loading}
          loaderProps={{ variant: "oval" }}
          color={combobox.dropdownOpened ? "indigo" : value ? "indigo" : "gray"}
          onClick={() => {
            combobox.toggleDropdown()
            if (combobox.dropdownOpened) setSource(value)
          }}
        >
          {label}
          {source ? `: ${allItems.find((item) => item.value === source)?.label || value}` : ""}
        </Button>
      </Combobox.Target>
      <Combobox.Dropdown miw={200}>
        <Combobox.Options>
          <Button
            leftSection={<IconX size={16} />}
            disabled={value?.length === 0}
            color="red"
            size="compact-sm"
            variant="subtle"
            fullWidth
            onClick={() => {
              setValue("")
              setSource(undefined)
              combobox.closeDropdown()
            }}
          >
            {t("Clear")}
          </Button>
          <ScrollArea.Autosize mah={300}>
            {groups.length > 0
              ? groups.map((group) => (
                  <Combobox.Group key={group.group} label={group.group}>
                    {group.items.map((item) => (
                      <Combobox.Option key={item.value} value={item.value} active={value === item.value}>
                        <Group gap="xs" wrap="nowrap">
                          <Text inherit mr="auto" fw={item.value == value ? 700 : 500}>
                            {item.label}
                          </Text>
                          {item.value == value && <IconCheck size={16} />}
                        </Group>
                      </Combobox.Option>
                    ))}
                  </Combobox.Group>
                ))
              : values.map((item) => (
                  <Combobox.Option key={item.value} value={item.value} active={value === item.value}>
                    <Group gap="xs" wrap="nowrap">
                      <Text inherit mr="auto" fw={item.value == value ? 700 : 500}>
                        {item.label}
                      </Text>
                      {item.value == value && <IconCheck size={16} />}
                    </Group>
                  </Combobox.Option>
                ))}
          </ScrollArea.Autosize>
          {value != source && (
            <Button
              leftSection={<IconChecks size={16} />}
              variant="subtle"
              size="compact-sm"
              fullWidth
              onClick={() => {
                setSource(value)
                combobox.closeDropdown()
              }}
            >
              {t("Apply")}
            </Button>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
